import { PsHttpService } from '@patsnap/tahiti-core';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { PatSnapSuccessResponseData } from '@patsnap/tahiti-core';
import type {
    AsyncTaskData,
    ComponentBindLocateRequest,
    UpdateComponentBindLocateRequest,
    ComponentBindLocateResponseData,
} from '../component-bindlocate-api';
import type {
    NoveltyAiSearchApi,
    ComponentExtractBindLocateRequest,
    ComponentExtractBindLocateResponseData,
    NoveltyAiSearchStreamEvent,
    NoveltyAiSearchStreamRequest,
    NoveltyAiSearchTaskStream,
} from '../novelty-ai-search-api';
import { noveltyAiSearchApiUrlPrefix } from '@telecom/shared/domain-config';

type StreamRequestExecutor = (config: Record<string, any>) => Promise<unknown>;

interface ParsedSseFrame {
    id?: string;
    event?: string;
    data: string;
}

class NoveltyAiSearchTaskStreamImpl implements NoveltyAiSearchTaskStream {
    private _abortController?: AbortController;
    private _buffer = '';
    private _oldResponseLength = 0;
    private _closed = false;
    private _finished = false;
    private _onOpen: (response: Response) => void = () => {};
    private _onMessage: (data: NoveltyAiSearchStreamEvent) => void = () => {};
    private _onClose: () => void = () => {};
    private _onError: (err: any) => void = () => {};
    private _onFinish: () => void = () => {};
    private _onParseError: (error: any) => void = () => {};

    constructor(
        private readonly _request: StreamRequestExecutor,
        private readonly _url: string,
        private readonly _headers: Record<string, string> = {},
    ) {}

    async start(): Promise<void> {
        this._abortController = new AbortController();
        this._closed = false;
        this._finished = false;
        this._onOpen(new Response());

        await this._request({
            method: 'GET',
            url: this._url,
            headers: this._headers,
            onDownloadProgress: (progressEvent: { target?: { response?: string } }) => {
                this._handleProgress(progressEvent);
            },
            signal: this._abortController.signal,
            timeout: Number.POSITIVE_INFINITY,
        })
            .catch((error) => {
                if (!this._closed && !this._finished) {
                    this._onError(error);
                    throw error;
                }
            })
            .finally(() => {
                this._invokeClose();
            });
    }

    close(): void {
        this._abortController?.abort();
        this._invokeClose();
    }

    onOpen(callback: (response: Response) => void): this {
        this._onOpen = callback;
        return this;
    }

    onMessage(callback: (data: NoveltyAiSearchStreamEvent) => void): this {
        this._onMessage = callback;
        return this;
    }

    onClose(callback: () => void): this {
        this._onClose = callback;
        return this;
    }

    onError(callback: (err: any) => void): this {
        this._onError = callback;
        return this;
    }

    onFinish(callback: () => void): this {
        this._onFinish = callback;
        return this;
    }

    onParseError(callback: (error: any) => void): this {
        this._onParseError = callback;
        return this;
    }

    private _handleProgress(progressEvent: { target?: { response?: string } }) {
        const responseText = progressEvent.target?.response || '';
        if (responseText.length < this._oldResponseLength) {
            this._oldResponseLength = 0;
            this._buffer = '';
        }

        const chunk = responseText.slice(this._oldResponseLength);
        this._oldResponseLength = responseText.length;
        this._buffer += chunk;

        const frames = this._buffer.split(/\r?\n\r?\n/);
        this._buffer = frames.pop() || '';

        frames.forEach((frame) => this._handleFrame(frame));
    }

    private _handleFrame(frame: string) {
        const parsedFrame = this._parseFrame(frame);
        if (!parsedFrame) {
            return;
        }

        try {
            const parsedData = JSON.parse(parsedFrame.data);
            const data = this._normalizeEvent(parsedData, parsedFrame);
            this._onMessage(data);

            if (data.event_type === 'task_complete' || data.event_type === 'task_error') {
                this._finish();
            }
        } catch (error) {
            this._onParseError(error);
        }
    }

    private _parseFrame(frame: string): ParsedSseFrame | null {
        const lines = frame.split(/\r?\n/);
        const dataLines: string[] = [];
        const parsedFrame: Omit<ParsedSseFrame, 'data'> = {};

        lines.forEach((line) => this._parseFrameLine(line, parsedFrame, dataLines));

        if (!dataLines.length) {
            return null;
        }

        return {
            ...parsedFrame,
            data: dataLines.join('\n'),
        };
    }

    private _parseFrameLine(line: string, parsedFrame: Omit<ParsedSseFrame, 'data'>, dataLines: string[]) {
        if (!line || line.startsWith(':')) {
            return;
        }

        const { field, value } = this._parseFrameField(line);
        const fieldHandlers: Record<string, () => void> = {
            id: () => { parsedFrame.id = value; },
            event: () => { parsedFrame.event = value; },
            data: () => { dataLines.push(value); },
        };
        fieldHandlers[field]?.();
    }

    private _parseFrameField(line: string) {
        const colonIndex = line.indexOf(':');
        return {
            field: colonIndex >= 0 ? line.slice(0, colonIndex) : line,
            value: this._normalizeFrameFieldValue(line, colonIndex),
        };
    }

    private _normalizeFrameFieldValue(line: string, colonIndex: number) {
        if (colonIndex < 0) {
            return '';
        }

        const value = line.slice(colonIndex + 1);
        return value.startsWith(' ') ? value.slice(1) : value;
    }

    private _normalizeEvent(parsedData: unknown, parsedFrame: ParsedSseFrame): NoveltyAiSearchStreamEvent {
        const data = typeof parsedData === 'object' && parsedData !== null
            ? parsedData as Record<string, unknown>
            : { message: String(parsedData) };
        const eventType = String(data.event_type || parsedFrame.event || 'message');

        return {
            ...data,
            event_type: eventType,
            event: parsedFrame.event || eventType,
            event_id: parsedFrame.id,
        } as NoveltyAiSearchStreamEvent;
    }

    private _finish() {
        if (this._finished) {
            return;
        }

        this._finished = true;
        this._onFinish();
        this.close();
    }

    private _invokeClose() {
        if (this._closed) {
            return;
        }

        this._closed = true;
        this._onClose();
    }
}

function buildNoveltyAiSearchStreamBaseUrl() {
    return noveltyAiSearchApiUrlPrefix.replace(/\/v2\/novelty\/?$/, '/api');
}

@injectable()
export class NoveltyAiSearchApiImpl
    extends PsHttpService
    implements NoveltyAiSearchApi {

    get options() {
        return {
            baseURL: noveltyAiSearchApiUrlPrefix,
        };
    }

    async componentExtractBindLocate(data: ComponentExtractBindLocateRequest): Promise<AsyncTaskData<ComponentExtractBindLocateResponseData>> {
        const result = await this.post<PatSnapSuccessResponseData<AsyncTaskData<ComponentExtractBindLocateResponseData>>>({
            url: 'component_extract_bindlocate',
            data,
            headers: {
                'x-result-id': data.task_id,
            },
        });
        return result.data;
    }

    async componentBindLocate(data: ComponentBindLocateRequest): Promise<AsyncTaskData<ComponentBindLocateResponseData>> {
        const result = await this.post<PatSnapSuccessResponseData<AsyncTaskData<ComponentBindLocateResponseData>>>({
            url: 'component_bindlocate',
            data,
        });
        return result.data;
    }

    async updateComponentBindLocate(data: UpdateComponentBindLocateRequest): Promise<ComponentBindLocateResponseData> {
        const result = await this.put<PatSnapSuccessResponseData<ComponentBindLocateResponseData>>({
            url: 'component_bindlocate',
            data,
        });
        return result.data;
    }

    async getTaskStream(data: NoveltyAiSearchStreamRequest): Promise<NoveltyAiSearchTaskStream> {
        const headers: Record<string, string> = {};
        if (data.lastEventId !== undefined && data.lastEventId !== null) {
            headers['Last-Event-ID'] = String(data.lastEventId);
        }

        return new NoveltyAiSearchTaskStreamImpl(
            this.request.bind(this),
            `${buildNoveltyAiSearchStreamBaseUrl()}/task/${encodeURIComponent(data.taskId)}/stream`,
            headers,
        );
    }
}
